import { createClient, type SupabaseClient } from "@supabase/supabase-js";
import { createAuthStorage } from "./authStorage";

const supabaseUrl = import.meta.env.VITE_SUPABASE_URL as string | undefined;
const supabaseAnonKey = import.meta.env.VITE_SUPABASE_ANON_KEY as string | undefined;

if (!supabaseUrl || !supabaseAnonKey) {
  // Fail loudly in dev so missing env config is obvious.
  // In prod this prevents the app from silently using `undefined` URLs.
  // eslint-disable-next-line no-console
  console.error(
    "[supabase] Missing VITE_SUPABASE_URL or VITE_SUPABASE_ANON_KEY. Create .env.local from .env.example."
  );
}

export type Database = {
  public: {
    Tables: {
      profiles: {
        Row: {
          id: string;
          name: string | null;
          // RESTRICTED COLUMNS — email / phone / date_of_birth /
          // parent_email / parent_name / parental_consent_token.
          //
          // These are still WRITABLE by the owner (onboarding sets them),
          // but SELECT on them was revoked from the `authenticated` role by
          // supabase/profiles_pii_and_rls_hardening_migration.sql. Putting
          // any of them in a .from("profiles").select(...) list — or in a
          // filter such as .eq("phone", …) — now fails with
          // "permission denied for column".
          //
          // To READ them, query the relationship-scoped view instead:
          //   supabase.from("profile_contacts").select("id, date_of_birth")
          // which returns rows only for the owner, a linked parent, an
          // actively-connected academy, or a verified scout who has
          // shortlisted the player. parental_consent_token is exposed
          // nowhere — the Edge Function reads it with the service key.
          //
          // They are kept on this Row type because Insert/Update derive
          // from it. Use PROFILE_SAFE_COLUMNS for select lists.
          email: string | null;
          phone: string | null;
          /** Under-18 players' guardian phone (E.164). Write-only to clients:
           *  SELECT is revoked like phone — see
           *  supabase/profiles_parent_phone_migration.sql. */
          parent_phone: string | null;
          /** Server-managed, read-only to clients. Always false today: the
           *  phone number is self-declared and there is no OTP. A client
           *  that sends it is refused by the guard trigger in
           *  supabase/profiles_phone_capture_migration.sql. Never put this
           *  in an .update() payload. */
          phone_verified: boolean;
          role: "player" | "scout" | "academy" | "parent" | null;
          sport: string | null;
          gender: string | null;
          age_group: string | null;
          region: string | null;
          location: string | null;
          academy: string | null;
          avatar_url: string | null;
          onboarding_complete: boolean;
          profile_score: number | null;
          total_videos: number | null;
          preferred_format: string | null;
          position: string | null;
          verification_submitted: boolean | null;
          is_verified: boolean | null;
          date_of_birth: string | null;
          parental_consent_status: "not_required" | "pending" | "approved" | "rejected";
          parent_email: string | null;
          parent_name: string | null;
          parental_consent_token: string | null;
          email_verified_at: string | null;
          is_suspended: boolean;
          follower_count: number;
          following_count: number;
          post_count: number;
          discovery_completed: boolean;
          academy_access_enabled: boolean;
          tour_completed_at: string | null;
          tour_skipped_at: string | null;
          // Extra monthly AI analyses granted by referrals. Folded into
          // the per-tier limit by the can_score_video() RPC.
          // Server-managed: a client UPDATE of this column is rejected by
          // the guard_report_quota_columns trigger.
          bonus_analyses: number;
          // Strength Card reports generated in the window that started at
          // reports_period_start. Written only by consume_report_quota()
          // from the analyze-video Edge Function; the same guard trigger
          // rejects client writes. Read via the can_score_video() RPC.
          reports_generated: number;
          reports_period_start: string;
          created_at: string;
          updated_at: string | null;
        };
        Insert: Partial<Database["public"]["Tables"]["profiles"]["Row"]> & { id: string };
        Update: Partial<Database["public"]["Tables"]["profiles"]["Row"]>;
      };
      videos: {
        Row: {
          id: string;
          player_id: string;
          storage_path: string;
          original_filename: string | null;
          title: string | null;
          file_size_bytes: number | null;
          status: "uploaded" | "analyzing" | "scored" | "failed";
          /** Server-managed (service role only — guard trigger in
           *  supabase/video_analysis_failure_migration.sql). */
          failure_code: "no_cricket_action" | "model_unavailable" | "unsupported_drill" | null;
          /** Server-managed analysis lock; set while a model run is in flight. */
          analysis_started_at: string | null;
          uploaded_by_academy_id: string | null;
          uploaded_by_player: boolean;
          is_hidden: boolean;
          created_at: string;
        };
        Insert: Partial<Database["public"]["Tables"]["videos"]["Row"]> & {
          player_id: string;
          storage_path: string;
        };
        Update: Partial<Database["public"]["Tables"]["videos"]["Row"]>;
      };
      strength_cards: {
        Row: {
          id: string;
          player_id: string;
          video_id: string;
          overall_score: number | null;
          skills: Record<string, number> | null;
          insights: string[] | null;
          suggestions: string[] | null;
          /** Full upgraded Groq analysis (phases, drills, match intel, etc.). */
          analysis: Record<string, unknown> | null;
          created_at: string;
        };
        Insert: Partial<Database["public"]["Tables"]["strength_cards"]["Row"]> & {
          player_id: string;
          video_id: string;
        };
        Update: Partial<Database["public"]["Tables"]["strength_cards"]["Row"]>;
      };
      shortlists: {
        Row: {
          id: string;
          scout_id: string;
          player_id: string;
          folder_id: string | null;
          created_at: string;
        };
        Insert: { scout_id: string; player_id: string; folder_id?: string | null };
        Update: Partial<Database["public"]["Tables"]["shortlists"]["Row"]>;
      };
      shortlist_folders: {
        Row: {
          id: string;
          scout_id: string;
          name: string;
          created_at: string;
        };
        Insert: { scout_id: string; name: string };
        Update: Partial<Database["public"]["Tables"]["shortlist_folders"]["Row"]>;
      };
      scout_verifications: {
        Row: {
          id: string;
          scout_id: string;
          id_document_path: string | null;
          certificate_path: string | null;
          status: string;
          submitted_at: string;
        };
        Insert: {
          scout_id: string;
          id_document_path?: string | null;
          certificate_path?: string | null;
          status?: string;
        };
        Update: Partial<Database["public"]["Tables"]["scout_verifications"]["Row"]>;
      };
      academies: {
        Row: {
          id: string;
          user_id: string;
          name: string | null;
          address: string | null;
          primary_contact_phone: string | null;
          primary_contact_country_code: string | null;
          phone_verified: boolean;
          email: string | null;
          email_verified: boolean;
          social_link: string | null;
          size_category:
            | "lt_25"
            | "25_50"
            | "51_100"
            | "101_200"
            | "200_plus"
            | null;
          sports_offered: string[];
          founded_year: number | null;
          head_coach_name: string | null;
          head_coach_credentials: string | null;
          head_coach_experience_years: number | null;
          logo_url: string | null;
          primary_color: string;
          secondary_color: string;
          tagline: string | null;
          instagram_handle: string | null;
          verification_status: "unverified" | "pending" | "verified" | "rejected";
          onboarding_completed: boolean;
          onboarding_step: number;
          subscription_status: "trial" | "active" | "past_due" | "cancelled";
          // Plan tier. null = Free (the post-migration default for new
          // academies). Normalise reads via @/lib/academyPlanTier.
          subscription_tier: "free" | "starter" | "growth" | "pro" | "enterprise" | null;
          notification_preferences: Record<string, unknown>;
          scout_visibility: boolean;
          created_at: string;
          updated_at: string;
        };
        Insert: Partial<Database["public"]["Tables"]["academies"]["Row"]> & { user_id: string };
        Update: Partial<Database["public"]["Tables"]["academies"]["Row"]>;
      };
      academy_alumni: {
        Row: {
          id: string;
          academy_id: string;
          name: string;
          year: number | null;
          progressed_to:
            | "state_team"
            | "ipl_academy"
            | "professional_club"
            | "college_cricket"
            | "national_team"
            | "other";
          progressed_to_other_text: string | null;
          created_at: string;
        };
        Insert: Partial<Database["public"]["Tables"]["academy_alumni"]["Row"]> & {
          academy_id: string;
          name: string;
          progressed_to: Database["public"]["Tables"]["academy_alumni"]["Row"]["progressed_to"];
        };
        Update: Partial<Database["public"]["Tables"]["academy_alumni"]["Row"]>;
      };
      academy_coaches: {
        Row: {
          id: string;
          academy_id: string;
          user_id: string | null;
          name: string;
          email: string | null;
          role: "head_coach" | "assistant_coach" | "specialist_coach";
          credentials: string | null;
          experience_years: number | null;
          status: "active" | "invited" | "inactive";
          invited_at: string | null;
          joined_at: string | null;
          created_at: string;
        };
        Insert: Partial<Database["public"]["Tables"]["academy_coaches"]["Row"]> & {
          academy_id: string;
          name: string;
          role: Database["public"]["Tables"]["academy_coaches"]["Row"]["role"];
        };
        Update: Partial<Database["public"]["Tables"]["academy_coaches"]["Row"]>;
      };
      academy_activation_codes: {
        Row: {
          id: string;
          academy_id: string;
          code: string;
          is_active: boolean;
          regenerated_count: number;
          last_regenerated_at: string | null;
          created_at: string;
        };
        Insert: Partial<Database["public"]["Tables"]["academy_activation_codes"]["Row"]> & {
          academy_id: string;
          code: string;
        };
        Update: Partial<Database["public"]["Tables"]["academy_activation_codes"]["Row"]>;
      };
      academy_player_connections: {
        Row: {
          id: string;
          academy_id: string;
          player_id: string;
          status: "pending" | "active" | "disconnected" | "rejected";
          connection_start_date: string | null;
          connection_end_date: string | null;
          requested_at: string;
          approved_at: string | null;
          rejected_at: string | null;
          disconnected_at: string | null;
          rejection_reason: string | null;
          face_recognition_consent: boolean;
          // Editable focus items for the scouting card's "Next Month Focus".
          next_month_focus: string[] | null;
        };
        Insert: Partial<Database["public"]["Tables"]["academy_player_connections"]["Row"]> & {
          academy_id: string;
          player_id: string;
        };
        Update: Partial<Database["public"]["Tables"]["academy_player_connections"]["Row"]>;
      };
      coach_quick_notes: {
        Row: {
          id: string;
          player_id: string;
          academy_id: string;
          coach_user_id: string;
          note_text: string;
          voice_note_url: string | null;
          is_hidden: boolean;
          created_at: string;
        };
        Insert: Partial<Database["public"]["Tables"]["coach_quick_notes"]["Row"]> & {
          player_id: string;
          academy_id: string;
          coach_user_id: string;
          note_text: string;
        };
        Update: Partial<Database["public"]["Tables"]["coach_quick_notes"]["Row"]>;
      };
      academy_documents: {
        Row: {
          id: string;
          academy_id: string;
          document_type:
            | "registration"
            | "gst"
            | "pan"
            | "address_proof"
            | "signatory_id";
          file_url: string;
          uploaded_at: string;
          verified: boolean;
          verified_at: string | null;
        };
        Insert: Partial<Database["public"]["Tables"]["academy_documents"]["Row"]> & {
          academy_id: string;
          document_type: Database["public"]["Tables"]["academy_documents"]["Row"]["document_type"];
          file_url: string;
        };
        Update: Partial<Database["public"]["Tables"]["academy_documents"]["Row"]>;
      };
      content_reports: {
        Row: {
          id: string;
          reporter_user_id: string | null;
          reported_content_type: "video" | "profile" | "note" | "message" | "post";
          reported_content_id: string;
          reason: "inappropriate" | "spam" | "harassment" | "fake_profile" | "illegal" | "other";
          reason_details: string | null;
          status: "pending" | "reviewed" | "actioned" | "dismissed";
          created_at: string;
          reviewed_at: string | null;
          reviewed_by: string | null;
        };
        Insert: {
          reporter_user_id: string;
          reported_content_type: Database["public"]["Tables"]["content_reports"]["Row"]["reported_content_type"];
          reported_content_id: string;
          reason: Database["public"]["Tables"]["content_reports"]["Row"]["reason"];
          reason_details?: string | null;
        };
        Update: Partial<Database["public"]["Tables"]["content_reports"]["Row"]>;
      };
      posts: {
        Row: {
          id: string;
          author_id: string;
          text: string;
          image_url: string | null;
          image_width: number | null;
          image_height: number | null;
          like_count: number;
          is_hidden: boolean;
          created_at: string;
        };
        Insert: {
          id?: string;
          author_id: string;
          text?: string;
          image_url?: string | null;
          image_width?: number | null;
          image_height?: number | null;
          like_count?: number;
          is_hidden?: boolean;
          created_at?: string;
        };
        // Only the `text` column is updatable by authenticated users.
        // Other fields are blocked by the post_update_restrictions trigger;
        // narrowing the type here surfaces that intent at the call site.
        Update: { text?: string };
      };
      post_likes: {
        Row: {
          post_id: string;
          user_id: string;
          created_at: string;
        };
        Insert: {
          post_id: string;
          user_id: string;
          created_at?: string;
        };
        Update: never;
      };
      follows: {
        Row: {
          follower_id: string;
          following_id: string;
          created_at: string;
        };
        Insert: {
          follower_id: string;
          following_id: string;
          created_at?: string;
        };
        Update: never;
      };
      subscription_plans: {
        Row: {
          id: string;
          name: string;
          tier: "free" | "pro" | "pro_plus" | "unlimited" | "enterprise";
          billing_period: "monthly" | "annual" | "forever";
          price_inr: number;
          features: string[];
          is_active: boolean;
          sort_order: number;
          created_at: string;
        };
        Insert: never;
        Update: never;
      };
      user_subscriptions: {
        Row: {
          id: string;
          user_id: string;
          plan_id: string;
          status: "active" | "cancelled" | "expired" | "pending";
          started_at: string;
          current_period_end: string | null;
          google_play_purchase_token: string | null;
          google_play_order_id: string | null;
          created_at: string;
          updated_at: string;
        };
        // Clients never write to user_subscriptions — the signup trigger
        // creates the free row and v1.1 webhooks will update it under
        // service role. Insert/Update marked never so accidental client
        // writes fail at typecheck.
        Insert: never;
        Update: never;
      };
      subscription_waitlist: {
        Row: {
          id: string;
          user_id: string | null;
          email: string;
          target_plan_id: "pro" | "pro_plus" | "unlimited";
          notified: boolean;
          created_at: string;
        };
        Insert: {
          user_id: string;
          email: string;
          target_plan_id: "pro" | "pro_plus" | "unlimited";
          notified?: boolean;
        };
        Update: never;
      };
      ai_scoring_usage: {
        Row: {
          id: string;
          user_id: string;
          scored_at: string;
          video_id: string | null;
          created_at: string;
        };
        Insert: {
          user_id: string;
          video_id?: string | null;
          scored_at?: string;
        };
        Update: never;
      };
      referral_codes: {
        Row: {
          id: string;
          player_id: string;
          code: string;
          created_at: string;
        };
        // Clients read their own row; the code is created via the
        // get_or_create_referral_code() RPC (SECURITY DEFINER), not a
        // direct insert.
        Insert: never;
        Update: never;
      };
      referral_uses: {
        Row: {
          id: string;
          referral_code_id: string;
          referred_player_id: string;
          referrer_player_id: string;
          /** DEPRECATED: retired ₹ credit. Not read or written. */
          reward_amount: number;
          /** Bonus Strength Cards granted to the referrer on confirmation. */
          reward_cards: number;
          /** 'redeemed' / 'expired' only appear on rows from the retired ₹ flow. */
          status: "pending" | "confirmed" | "redeemed" | "expired";
          confirmed_at: string | null;
          redeemed_at: string | null;
          created_at: string;
        };
        // Written only by apply_referral_code() / the confirm trigger.
        Insert: never;
        Update: never;
      };
      referral_balance: {
        Row: {
          id: string;
          player_id: string;
          balance_rupees: number;
          total_earned: number;
          total_redeemed: number;
          updated_at: string;
        };
        // DEPRECATED: the retired ₹ referral credit. Nothing reads or
        // writes this table any more (referral_rewards_v2_migration.sql).
        Insert: never;
        Update: never;
      };
      sessions: {
        Row: {
          id: string;
          academy_id: string;
          coach_id: string | null;
          session_date: string;
          batch_name: string | null;
          drill_type: string | null;
          notes: string | null;
          created_at: string;
        };
        Insert: {
          academy_id: string;
          coach_id?: string | null;
          session_date?: string;
          batch_name?: string | null;
          drill_type?: string | null;
          notes?: string | null;
        };
        Update: Partial<{
          batch_name: string | null;
          drill_type: string | null;
          notes: string | null;
          session_date: string;
        }>;
      };
      session_videos: {
        Row: {
          id: string;
          session_id: string;
          player_id: string | null;
          academy_id: string;
          uploaded_by: string | null;
          storage_path: string;
          thumbnail_path: string | null;
          duration_seconds: number | null;
          file_size_bytes: number | null;
          drill_type: string | null;
          analysis_status: "pending" | "processing" | "complete" | "failed";
          created_at: string;
        };
        Insert: {
          session_id: string;
          academy_id: string;
          storage_path: string;
          player_id?: string | null;
          uploaded_by?: string | null;
          thumbnail_path?: string | null;
          duration_seconds?: number | null;
          file_size_bytes?: number | null;
          drill_type?: string | null;
          analysis_status?: "pending" | "processing" | "complete" | "failed";
        };
        Update: Partial<{
          player_id: string | null;
          thumbnail_path: string | null;
          duration_seconds: number | null;
          analysis_status: "pending" | "processing" | "complete" | "failed";
          drill_type: string | null;
        }>;
      };
      coach_notes: {
        Row: {
          id: string;
          session_video_id: string | null;
          player_id: string;
          academy_id: string;
          coach_id: string | null;
          note_text: string;
          created_at: string;
          updated_at: string;
        };
        Insert: {
          player_id: string;
          academy_id: string;
          coach_id: string;
          note_text: string;
          session_video_id?: string | null;
        };
        Update: Partial<{
          note_text: string;
        }>;
      };
      video_player_tags: {
        Row: {
          id: string;
          session_video_id: string;
          player_id: string;
          tagged_by_user: string | null;
          tag_method: "manual" | "ai";
          confidence_score: number | null;
          created_at: string;
        };
        Insert: {
          session_video_id: string;
          player_id: string;
          tagged_by_user: string;
          tag_method?: "manual" | "ai";
          confidence_score?: number | null;
        };
        Update: never;
      };
      player_skill_scores: {
        Row: {
          id: string;
          player_id: string;
          academy_id: string;
          session_video_id: string | null;
          scored_at: string;
          scored_by: string | null;
          scoring_method: "manual" | "ai";
          batting_stance: number | null;
          backlift: number | null;
          bat_speed: number | null;
          head_position: number | null;
          bowling_action: number | null;
          wrist_position: number | null;
          run_up: number | null;
          follow_through: number | null;
          // Wicket-keeping axes (Prompt 6).
          glove_consistency: number | null;
          footwork_lateral: number | null;
          footwork_forward: number | null;
          recovery_time: number | null;
          stumping_speed: number | null;
          catch_positioning: number | null;
          // Fielding axes (Prompt 6).
          throwing_action: number | null;
          release_height: number | null;
          throw_accuracy: number | null;
          ground_fielding: number | null;
          diving_technique: number | null;
          reaction_time: number | null;
          created_at: string;
        };
        Insert: {
          player_id: string;
          academy_id: string;
          scored_by: string;
          session_video_id?: string | null;
          scored_at?: string;
          scoring_method?: "manual" | "ai";
          batting_stance?: number | null;
          backlift?: number | null;
          bat_speed?: number | null;
          head_position?: number | null;
          bowling_action?: number | null;
          wrist_position?: number | null;
          run_up?: number | null;
          follow_through?: number | null;
          glove_consistency?: number | null;
          footwork_lateral?: number | null;
          footwork_forward?: number | null;
          recovery_time?: number | null;
          stumping_speed?: number | null;
          catch_positioning?: number | null;
          throwing_action?: number | null;
          release_height?: number | null;
          throw_accuracy?: number | null;
          ground_fielding?: number | null;
          diving_technique?: number | null;
          reaction_time?: number | null;
        };
        Update: Partial<{
          session_video_id: string | null;
          batting_stance: number | null;
          backlift: number | null;
          bat_speed: number | null;
          head_position: number | null;
          bowling_action: number | null;
          wrist_position: number | null;
          run_up: number | null;
          follow_through: number | null;
          glove_consistency: number | null;
          footwork_lateral: number | null;
          footwork_forward: number | null;
          recovery_time: number | null;
          stumping_speed: number | null;
          catch_positioning: number | null;
          throwing_action: number | null;
          release_height: number | null;
          throw_accuracy: number | null;
          ground_fielding: number | null;
          diving_technique: number | null;
          reaction_time: number | null;
        }>;
      };
      academy_notifications: {
        Row: {
          id: string;
          academy_id: string;
          type: "report_generated" | "player_joined" | "trial_updated" | "score_added" | "note_added" | "video_pending_approval";
          title: string;
          body: string | null;
          read: boolean;
          related_player_id: string | null;
          related_entity_id: string | null;
          created_at: string;
        };
        // Rows are created by DB triggers + service-role calls. Clients
        // only UPDATE `read`. Insert from client is denied by RLS.
        Insert: never;
        Update: Partial<{ read: boolean }>;
      };
      player_academy_privacy: {
        Row: {
          id: string;
          player_id: string;
          academy_id: string;
          allow_video_access: boolean;
          allow_score_access: boolean;
          allow_notes_access: boolean;
          allow_attendance_access: boolean;
          allow_profile_access: boolean;
          allow_report_generation: boolean;
          created_at: string;
          updated_at: string;
        };
        Insert: {
          player_id: string;
          academy_id: string;
          allow_video_access?: boolean;
          allow_score_access?: boolean;
          allow_notes_access?: boolean;
          allow_attendance_access?: boolean;
          allow_profile_access?: boolean;
          allow_report_generation?: boolean;
        };
        Update: Partial<{
          allow_video_access: boolean;
          allow_score_access: boolean;
          allow_notes_access: boolean;
          allow_attendance_access: boolean;
          allow_profile_access: boolean;
          allow_report_generation: boolean;
        }>;
      };
      parent_player_links: {
        Row: {
          id: string;
          parent_id: string;
          player_id: string;
          academy_id: string;
          relationship: "father" | "mother" | "guardian";
          verified: boolean;
          created_at: string;
        };
        Insert: {
          parent_id: string;
          player_id: string;
          academy_id: string;
          relationship: "father" | "mother" | "guardian";
          verified?: boolean;
        };
        Update: never;
      };
      monthly_reports: {
        Row: {
          id: string;
          player_id: string;
          academy_id: string;
          period_month: string | null;
          report_month: string | null;
          pdf_url: string | null;
          pdf_storage_path: string | null;
          narrative_text: string | null;
          status: "draft" | "generating" | "complete" | "failed";
          generated_at: string | null;
          generated_by: string | null;
          created_at: string;
        };
        // Insert is performed by academy members to enqueue a report
        // (status='generating'); the Edge Function then UPDATEs it to
        // complete/failed under the service-role key.
        Insert: {
          player_id: string;
          academy_id: string;
          report_month: string;
          period_month?: string | null;
          status?: "draft" | "generating" | "complete" | "failed";
          generated_by?: string | null;
        };
        Update: Partial<{
          status: "draft" | "generating" | "complete" | "failed";
          pdf_url: string | null;
          pdf_storage_path: string | null;
          narrative_text: string | null;
          generated_at: string | null;
        }>;
      };
      academy_tournaments: {
        Row: {
          id: string;
          academy_id: string;
          name: string;
          location: string;
          opponent_name: string;
          players_participated: string;
          total_matches: number;
          created_by: string | null;
          created_at: string;
          updated_at: string;
        };
        Insert: {
          academy_id: string;
          name: string;
          location: string;
          opponent_name: string;
          players_participated: string;
          total_matches: number;
          created_by?: string | null;
        };
        Update: Partial<{
          name: string;
          location: string;
          opponent_name: string;
          players_participated: string;
          total_matches: number;
        }>;
      };
      tournament_matches: {
        Row: {
          id: string;
          tournament_id: string;
          academy_id: string;
          match_name: string;
          match_level: "Group Stage" | "Knockouts" | "Final";
          innings: "First" | "Second" | "Both";
          opponent: string;
          result: "Win" | "Defeat";
          stats_file_path: string | null;
          stats_file_type: "xlsx" | "pdf" | null;
          video_storage_path: string | null;
          analysis_status: "pending" | "processing" | "complete" | "failed";
          analysis_result: Record<string, unknown> | null;
          created_at: string;
        };
        Insert: {
          tournament_id: string;
          academy_id: string;
          match_name: string;
          match_level: "Group Stage" | "Knockouts" | "Final";
          innings: "First" | "Second" | "Both";
          opponent: string;
          result: "Win" | "Defeat";
          stats_file_path?: string | null;
          stats_file_type?: "xlsx" | "pdf" | null;
          video_storage_path?: string | null;
          analysis_status?: "pending" | "processing" | "complete" | "failed";
          analysis_result?: Record<string, unknown> | null;
        };
        Update: Partial<{
          match_name: string;
          match_level: "Group Stage" | "Knockouts" | "Final";
          innings: "First" | "Second" | "Both";
          opponent: string;
          result: "Win" | "Defeat";
          stats_file_path: string | null;
          stats_file_type: "xlsx" | "pdf" | null;
          video_storage_path: string | null;
          analysis_status: "pending" | "processing" | "complete" | "failed";
          analysis_result: Record<string, unknown> | null;
        }>;
      };
      promote_renders: {
        Row: {
          id: string;
          academy_id: string;
          type: "reel" | "spotlight" | "wrapped" | "card";
          player_id: string | null;
          render_id: string | null;
          output_url: string | null;
          status: "queued" | "rendering" | "done" | "failed";
          caption: string | null;
          caption_options: string[] | null;
          period: string | null;
          created_at: string;
          updated_at: string;
        };
        Insert: Partial<Database["public"]["Tables"]["promote_renders"]["Row"]> & {
          academy_id: string;
          type: "reel" | "spotlight" | "wrapped" | "card";
        };
        Update: Partial<Database["public"]["Tables"]["promote_renders"]["Row"]>;
      };
      match_player_analysis: {
        Row: {
          id: string;
          match_id: string;
          tournament_id: string;
          academy_id: string;
          player_id: string | null;
          player_name: string;
          total_matches: number | null;
          total_runs: number | null;
          batting_average: number | null;
          strike_rate: number | null;
          total_wickets: number | null;
          bowling_economy: number | null;
          total_tournaments: number | null;
          total_seasons: number | null;
          normal_average: number | null;
          pressure_average: number | null;
          pressure_delta_pct: number | null;
          match_impact_score: number | null;
          impact_context: string | null;
          consistency_score: number | null;
          match_scores: number[] | null;
          oqi_adjusted_average: number | null;
          oqi_raw_average: number | null;
          performance_vs_top: Record<string, number> | null;
          vs_pace_avg: number | null;
          vs_spin_avg: number | null;
          vs_leftarm_avg: number | null;
          vs_rightarm_avg: number | null;
          vs_pace_sr: number | null;
          vs_spin_sr: number | null;
          matchup_weakness: string | null;
          season_1_avg: number | null;
          season_2_avg: number | null;
          season_3_avg: number | null;
          trajectory_pct_change: number | null;
          trajectory_direction: "improving" | "stable" | "declining" | null;
          trial_readiness_score: number | null;
          readiness_consistency: number | null;
          readiness_pressure: number | null;
          readiness_opposition: number | null;
          readiness_trajectory: number | null;
          catches_per_match: number | null;
          runouts_per_match: number | null;
          misfields_per_match: number | null;
          fielding_observation: string | null;
          is_hidden_talent: boolean | null;
          hidden_talent_reasons: string[] | null;
          ai_narrative: string | null;
          analysis_version: number | null;
          created_at: string;
          updated_at: string;
        };
        Insert: Partial<Database["public"]["Tables"]["match_player_analysis"]["Row"]> & {
          match_id: string;
          tournament_id: string;
          academy_id: string;
          player_name: string;
        };
        Update: Partial<Database["public"]["Tables"]["match_player_analysis"]["Row"]>;
      };
      tournament_player_stats: {
        Row: {
          id: string;
          tournament_id: string;
          player_id: string | null;
          academy_id: string;
          total_matches: number | null;
          total_runs: number | null;
          highest_score: number | null;
          batting_average: number | null;
          strike_rate: number | null;
          total_wickets: number | null;
          best_bowling: string | null;
          bowling_economy: number | null;
          trial_readiness_score: number | null;
          pressure_performance_index: number | null;
          consistency_score: number | null;
          is_hidden_talent: boolean | null;
          updated_at: string;
        };
        Insert: Partial<Database["public"]["Tables"]["tournament_player_stats"]["Row"]> & {
          tournament_id: string;
          academy_id: string;
        };
        Update: Partial<Database["public"]["Tables"]["tournament_player_stats"]["Row"]>;
      };
      squad_reports: {
        Row: {
          id: string;
          academy_id: string;
          age_group: string;
          report_month: string;
          pdf_storage_path: string | null;
          narrative_text: string | null;
          status: "draft" | "generating" | "complete" | "failed";
          generated_at: string | null;
          generated_by: string | null;
          created_at: string;
        };
        Insert: {
          academy_id: string;
          age_group: string;
          report_month: string;
          status?: "draft" | "generating" | "complete" | "failed";
          generated_by?: string | null;
          pdf_storage_path?: string | null;
          narrative_text?: string | null;
        };
        Update: Partial<{
          status: "draft" | "generating" | "complete" | "failed";
          pdf_storage_path: string | null;
          narrative_text: string | null;
          generated_at: string | null;
        }>;
      };
      video_analysis_jobs: {
        Row: {
          id: string;
          session_video_id: string;
          academy_id: string;
          analysis_type: "bowling" | "batting" | "keeping" | "fielding" | "unknown";
          status: "pending" | "processing" | "complete" | "failed";
          result_json: Record<string, unknown> | null;
          pitchiq_job_id: string | null;
          error_message: string | null;
          created_at: string;
          updated_at: string;
        };
        // Auto-created via DB trigger; manual re-queue via academy
        // INSERT (UNIQUE on session_video_id makes it idempotent).
        Insert: {
          session_video_id: string;
          academy_id: string;
          analysis_type?: "bowling" | "batting" | "keeping" | "fielding" | "unknown";
          status?: "pending" | "processing" | "complete" | "failed";
        };
        Update: never;
      };
      reference_videos: {
        Row: {
          id: string;
          shot_type: "Batting" | "Bowling" | "Fielding" | "Wicketkeeping";
          player_name: string;
          video_url: string;
          thumbnail_url: string | null;
          created_at: string;
        };
        Insert: never;
        Update: never;
      };
      pending_video_approvals: {
        Row: {
          id: string;
          player_id: string;
          academy_id: string;
          video_storage_path: string;
          video_metadata: Record<string, unknown> | null;
          status: "pending" | "accepted" | "rejected";
          rejection_reason: string | null;
          created_at: string;
          updated_at: string | null;
        };
        Insert: {
          player_id: string;
          academy_id: string;
          video_storage_path: string;
          video_metadata?: Record<string, unknown> | null;
          status?: "pending" | "accepted" | "rejected";
          rejection_reason?: string | null;
        };
        Update: Partial<{
          status: "pending" | "accepted" | "rejected";
          rejection_reason: string | null;
        }>;
      };
      session_attendance: {
        Row: {
          id: string;
          session_id: string;
          player_id: string;
          academy_id: string;
          status: "present" | "absent" | "late";
          marked_by: string | null;
          marked_at: string;
        };
        Insert: {
          session_id: string;
          player_id: string;
          academy_id: string;
          status?: "present" | "absent" | "late";
          marked_by?: string | null;
        };
        Update: Partial<{
          status: "present" | "absent" | "late";
          marked_at: string;
        }>;
      };
    };
    Views: {
      academy_pending_requests: {
        Row: {
          connection_id: string;
          academy_id: string;
          player_id: string;
          requested_at: string;
          player_name: string | null;
          // player_email was dropped from this view — it now runs with
          // security_invoker, and the caller has no privilege on
          // profiles.email. Nothing rendered it.
          player_avatar_url: string | null;
          player_sport: string | null;
          player_location: string | null;
          player_profile_score: number | null;
        };
      };
      /** Relationship-scoped read path for the restricted profile columns.
       *  A row appears only when the caller is the owner, a linked parent,
       *  an actively-connected academy, or a verified scout who has
       *  shortlisted the player. parent_email / parent_name additionally
       *  come back null for scouts, and phone is null for everyone except
       *  the row's owner. Created by
       *  supabase/profiles_pii_and_rls_hardening_migration.sql. */
      profile_contacts: {
        Row: {
          id: string;
          email: string | null;
          date_of_birth: string | null;
          /** OWNER ONLY — null on every row but your own, gated by
           *  `case when is_self` in the view. Narrower than parent_*,
           *  which linked parents and academies can also see. */
          phone: string | null;
          parent_email: string | null;
          parent_name: string | null;
        };
      };
    };
  };
};

/** Every profiles column the `authenticated` role may SELECT.
 *
 *  Use this instead of "*" — a bare select("*") on profiles now fails,
 *  because Postgres expands it to columns the role has no privilege on.
 *  The restricted six (email, phone, date_of_birth, parent_email,
 *  parent_name, parental_consent_token) are deliberately absent; read
 *  them through the profile_contacts view. */
export const PROFILE_SAFE_COLUMNS =
  "id, name, role, sport, gender, age_group, region, location, academy, " +
  "avatar_url, organization, scout_role, onboarding_complete, profile_score, " +
  "total_videos, preferred_format, position, verification_submitted, " +
  "is_verified, parental_consent_status, email_verified_at, is_suspended, " +
  "follower_count, following_count, post_count, discovery_completed, " +
  "academy_access_enabled, tour_completed_at, tour_skipped_at, " +
  "bonus_analyses, created_at, updated_at";

/** Call a Postgres RPC by name.
 *
 *  The hand-written Database type above has no Functions map, so the typed
 *  supabase.rpc() signature narrows its argument to `never` and rejects
 *  every call. Existing call sites work around that with an inline cast
 *  (see groqAnalysis.ts); this wraps the same cast once so new call sites
 *  don't each repeat it. Drop it if the Database type ever grows a
 *  generated Functions map. */
export async function callRpc<T = unknown>(
  fn: string,
  args?: Record<string, unknown>,
): Promise<{ data: T | null; error: { message: string } | null }> {
  const invoke = supabase.rpc as unknown as (
    name: string,
    params?: Record<string, unknown>,
  ) => Promise<{ data: T | null; error: { message: string } | null }>;
  return invoke(fn, args);
}

export const SCOUT_DOCS_BUCKET = "scout-documents";

export const supabase: SupabaseClient<Database> = createClient<Database>(
  supabaseUrl ?? "http://invalid.local",
  supabaseAnonKey ?? "invalid",
  {
    auth: {
      persistSession: true,
      autoRefreshToken: true,
      detectSessionInUrl: true,
      // Native: Capacitor Preferences, which is app-owned storage and so
      // outlives WebView data being cleared or evicted. Web: undefined,
      // keeping supabase-js's own localStorage. See src/lib/authStorage.ts.
      storage: createAuthStorage(),
      // PKCE flow is required for the Capacitor-native OAuth path —
      // we open the OAuth URL in the system browser, catch the
      // redirect via a custom-scheme deep link, and call
      // supabase.auth.exchangeCodeForSession(code) from the deep-link
      // handler. PKCE is also the safer default for web; email +
      // password sign-in is unaffected (no OAuth involved).
      flowType: "pkce",
    },
  }
);

export const VIDEO_BUCKET = "player-videos";
export const MAX_VIDEO_BYTES = 200 * 1024 * 1024; // 200MB

// Academy session videos — separate bucket from player-uploaded videos
// so RLS / size / MIME limits can diverge. 500 MB cap (longer-form
// match-day clips); MIME-restricted server-side in the bucket config.
export const ACADEMY_VIDEO_BUCKET = "academy-videos";
export const MAX_ACADEMY_VIDEO_BYTES = 500 * 1024 * 1024;

// Tournament match stats (Excel / PDF). Files live at
// {academy_id}/{tournament_id}/{timestamp}-{filename}. Create the bucket
// in the Supabase dashboard (private) before this feature is used.
export const TOURNAMENT_STATS_BUCKET = "tournament-stats";
export const MAX_TOURNAMENT_STATS_BYTES = 25 * 1024 * 1024; // 25MB

// Tournament match footage (MP4/MOV/AVI). Files live at
// {academy_id}/{tournament_id}/{timestamp}-{filename}. Create the bucket
// (private) in the Supabase dashboard before this feature is used.
export const TOURNAMENT_VIDEOS_BUCKET = "tournament-videos";
export const MAX_TOURNAMENT_VIDEO_BYTES = 500 * 1024 * 1024; // 500MB

/**
 * Flip profiles.discovery_completed to true for the given user. Called
 * from the Discovery onboarding step when the user taps Continue / Skip.
 * Non-fatal — if the write fails, the user can still proceed (we don't
 * want to block them on a denormalized flag) and the next session will
 * just bounce them through Discovery once more.
 */
export async function markDiscoveryComplete(userId: string): Promise<void> {
  const { error } = await supabase
    .from("profiles")
    .update({ discovery_completed: true })
    .eq("id", userId);
  if (error) {
    // eslint-disable-next-line no-console
    console.warn("[supabase] markDiscoveryComplete failed", error);
  }
}
